import React, { useContext, useState, useEffect } from "react";
import { ConfigurationContext } from "contexts";
import {
  useNetworkDetection,
  useSpeedTestDetection,
  useSteps,
  useLogger,
  useAnalytics,
} from "hooks";
import Error from "./Error";
import MessageScanError from "./MessageScanError";
import OoklaTests from "./OoklaTests";
import NetworkTests from "./NetworkTests";

const stepName = "networkDetection";

/**
 * Network Detection via detection library.
 *
 * @param {object} props
 */

const StepNetworkDetection = () => {
  //  State can be:
  //    1. ooklaTests - running Ookla speed test.
  //    2. networkTests - running network tests (Error occurred in ookla).
  //    3. timeoutError - Scan ran out of time.
  //    4. scanError - Scan data error.

  const [state, setState] = useState({});

  let config = useContext(ConfigurationContext);
  let { steps, setCompleted, nextStep, previousStep } = useSteps();
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  let logger = useLogger();
  const analytics = useAnalytics();
  let [error, setError] = useState(false);

  /** Finish and clean up for step. Move onto the next step.
   *
   */
  const finish = () => {
    setCompleted(stepName);
    nextStep();
  };

  /** Determine if this step should display and run.
   *
   */
  const canRun = () => {
    try {
      return (
        config.steps.networkDetection && !steps.state.networkDetection.completed
      );
    } catch (error) {
      console.log(`Step ${stepName} not found.`);
      return false;
    }
  };

  /**
   *
   * Is Ookla available?
   *
   */
  // const isOoklaAvailableAsync = async () => {
  //   try {
  //     const response = await fetch("https://srlx.speedtestcustom.com");
  //     if (response.ok) {
  //       return false; //true;
  //     }
  //     return false;
  //   } catch (error) {
  //     return false;
  //   }
  // };

  /** Done handler for network detection.
   *  Determines if the detection was successful or with errors.
   */
  const onNetworkDetectionDone = (evt) => {
    if (!evt.success) {
      //setError(true);

      // Timeout.
      if (evt.error && evt.error.id === "TimeoutError") {
        logger.error("Network detection timed out", "NetworkDetectionTimeout");
        analytics.timedOut("network");
        setState({ timeoutError: true });
      }
      // Scan error.
      if (evt.error && evt.error.id === "ScanError") {
        logger.error(
          "Network detection experienced an error",
          "NetworkDetectionError"
        );
        setState({ scanError: true });
      }
    } else {
      // Success.
      logger.warning(
        "Network detection was successful",
        "NetworkDetectionSuccess"
      );
      finish();
    }
  };

  /** Handler for detection library progress.
   *
   */
  const onProgress = (msg) => {
    if (msg.id === 45) {
      setDownloadProgress(Math.floor(msg.value));
    } else if (msg.id === 50) {
      setUploadProgress(Math.floor(msg.value));
    }
  };

  /**
   * Determines if speed detection is required.
   *
   * @returns true if speed detection.
   */
  // const isSpeedDetection = () => {
  //   // Only if there was an error in speed test.

  //   return config.detection.standard;

  //   //return config.detection.networkSpeed;
  //   //    return steps.state.speedTestDetection.error;
  // };

  /**
   * Determines if latency detection is required.
   *
   * @returns true if latency detection.
   */
  // const isLatencyDetection = () => {
  //   return config.detection.networkLatency | config.detection.networkSpeed;
  // };

  let runNetworkDetection = useNetworkDetection(
    onProgress,
    onNetworkDetectionDone,
    () => {},
    config.detection.standardLatency, //   isLatencyDetection(),
    config.detection.standardSpeed || error // isSpeedDetection() && error
  );

  const onOoklaDetectionDone = (evt) => {
    if (!evt.success) {
      setError(true);

      if (evt.error && evt.error.id === "TimeoutError") {
        logger.error("Ookla detection timed out", "OoklaTimeout");
        analytics.timedOut("network");
        //     setState({ timeoutError: true });
      }
      if (evt.error && evt.error.id === "ScanError") {
        logger.error("Ookla detection experienced an error", "OoklaError");
        analytics.failed("OoklaError");
        //    setState({ scanError: true });
      }
      // Change the state to do the fall back detection.
      setState({ networkTests: true });
      //runNetworkDetection();
    } else {
      if (config.detection.standardLatency) {
        //                isLatencyDetection()) {
        // Got to run the network testing for the latency.
        setState({ networkTests: true });
      } else {
        finish();
      }
    }
  };

  let runOoklaDetection = useSpeedTestDetection(
    () => {},
    onOoklaDetectionDone,
    () => {}
  );

  // Set the initial state.
  useEffect(
    () => {
      // let ooklaIsAvailable = true;

      if (canRun()) {
        // if (config.detection.ookla)
        // {
        //   (async () => {
        //     ooklaIsAvailable = await isOoklaAvailableAsync();
        //   })();

        // // Is the Ookla network test available? If not then turn on all standard detection and proceed.
        // if (!ooklaIsAvailable)
        //   {
        //     config.detection.standardSpeed = true;
        //     config.detection.standardLatency = true;
        //     config.detection.ookla = false;
        //   }
        // }

        // Run either ookla or standard.
        if (config.detection.ookla) {
          //          logger.error("running ookla", "");

          setState({ ooklaTests: true });
        } else {
          //          logger.error("running standard", "");

          setState({ networkTests: true });
        }
      } else {
        // No testing.
        finish();
      }
    },
    [] // Only once.
  );

  // Run the correct detection.
  useEffect(() => {
    if (canRun()) {
      if (state.ooklaTests) {
        //   console.log("Running ookla");
        runOoklaDetection();
      } else if (state.networkTests) {
        //  console.log("Running network");
        runNetworkDetection();
      }
    }
  }, [state]);

  /**
   *  Retry handler. Restart this step.
   */
  const onRetry = () => {
    previousStep();
  };

  /**
   *  Cancel detection. Go to error page or go back.
   */
  const onCancel = () => {
    if (config.error && config.error.length > 0) {
      window.location = config.error;
    } else {
      // No error url so go back.
      window.history.back();
    }
    // // Set the appropriate step error state.
    // if (state.scanError) {
    //   setError({ networkDetection: { scanError: true } });
    // } else if (state.timeoutError) {
    //   setError({ networkDetection: { timeoutError: true } });
    // }
    // // Onto the next step.
    // finish();
  };

  return (
    <div className="mt-3 text-center">
      {/* <Title style={{ textAlign: "center" }}>Network Tests</Title>
      <Title level={4}>Testing network latency and upload and download speeds...</Title> */}

      {state.ooklaTests && <OoklaTests />}

      {state.networkTests && (
        <NetworkTests
          latencyProgress={100}
          uploadProgress={uploadProgress}
          downloadProgress={downloadProgress}
        />
      )}

      {/* {state.testingMin && <ProgressMin latencyProgress={100} />}
      {state.testingAll && (
        <ProgressAll
          latencyProgress={0}
          downloadSpeedProgress={downloadProgress}
          uploadSpeedProgress={uploadProgress}
        />
      )} */}

      {state.timeoutError && (
        <Error onRetry={onRetry} onContinue={onCancel}>
          <MessageScanError />
        </Error>
      )}
      {state.scanError && (
        <Error onRetry={onRetry} onContinue={onCancel}>
          <MessageScanError />
        </Error>
      )}
    </div>
  );
};

export default StepNetworkDetection;
